import { previewObjectivesAction, type AnalyticsEvent } from '../../core/events';
import { useState, type ReactElement } from 'react';
import type { Analysis, ChecklistItem, Finding, StandardsChecklist } from '../api';
import type { ReportVariant } from '../analytics/client';
import { standardLabel, LEARNING_COMMONS_STANDARDS_URL } from '../../core/links';

/**
 * The reading, in two directions.
 *
 * "Your plan" is the teacher's objectives as the spine — each one a card with a verdict,
 * any matched standard nested inside it. "The state's checklist" is the reverse: every
 * expectation the state publishes at this grade, grouped by topic, marked with what the
 * plan touched — the standard has ten items under this topic and your plan covers six.
 *
 * No numbers on the surface in either direction. Scores, bands and ranks are our
 * machinery; a teacher reads verdict words and coloured circles. Icon and word travel
 * together everywhere: colour alone excludes colourblind readers.
 */

export interface ReportProps {
  analysis: Analysis;
  extractedText: string | null;
  /** The report experiment: document order, or needs-attention first. */
  variant: ReportVariant;
  onStartOver: () => void;
  /** Back to the profile form. Offered where the reading suggests it is the wrong lens. */
  onChangeProfile: () => void;
  /** The toggle is a product question — does anyone use the reverse reading? — so it is tracked. */
  onViewToggled?: (view: 'plan' | 'standards') => void;
  /**
   * Remove the saved report and any remaining temporary objects. The second click
   * is the confirmation, in the same place, so it cannot be mistaken for "start over".
   */
  onDelete?: () => void;
  deleting?: boolean;
  onTrack?: (event: AnalyticsEvent) => void;
}

type Outcome = Finding['band'] | 'none';
type ViewName = 'plan' | 'standards';
type Filter = Outcome | 'all';

/** Weakest first, so "needs attention" sorts to the top of the grouped variant. */
const OUTCOME_ORDER: Outcome[] = ['none', 'weak', 'partial', 'covered'];

const OUTCOME_LABEL: Record<Outcome, string> = {
  covered: 'Covered',
  partial: 'Related match',
  weak: 'Possibly related — check it',
  none: 'Not matched',
};

/** Chip-sized versions of the same verdicts, for the filter row. */
const OUTCOME_SHORT: Record<Outcome, string> = {
  covered: 'Covered',
  partial: 'Related',
  weak: 'Check it',
  none: 'Not matched',
};

/**
 * The glyphs are drawn, not typed. ✓ and ✕ as font characters sit wherever the font's
 * metrics put them, which at chip size was visibly off-centre and fat — an SVG path is
 * centred by geometry and identical everywhere.
 */
const GLYPH: Record<Outcome, { name: string; path: ReactElement }> = {
  covered: { name: 'check', path: <path d="M3.6 8.4 L6.6 11.4 L12.4 4.6" /> },
  // A possible match, distinguished from covered by amber and its verdict text.
  partial: { name: 'check', path: <path d="M3.6 8.4 L6.6 11.4 L12.4 4.6" /> },
  // genuinely a warning: judge it yourself
  weak: { name: 'bang', path: <path d="M8 3.6 L8 9.2 M8 12.1 L8 12.4" /> },
  none: { name: 'cross', path: <path d="M4.6 4.6 L11.4 11.4 M11.4 4.6 L4.6 11.4" /> },
};

/** The glyph alone. aria-hidden — the verdict word is always nearby and is the content. */
function Icon({ outcome }: { outcome: Outcome }) {
  const glyph = GLYPH[outcome];
  return (
    <span className="verdict-icon" data-outcome={outcome} aria-hidden="true">
      <svg viewBox="0 0 16 16" data-glyph={glyph.name} fill="none" stroke="currentColor">
        {glyph.path}
      </svg>
    </span>
  );
}

function Verdict({ outcome, title }: { outcome: Outcome; title?: string }) {
  return (
    <span className="verdict" title={title}>
      <Icon outcome={outcome} />
      {OUTCOME_LABEL[outcome]}
    </span>
  );
}

/** What each verdict means, in the teacher's terms. Shown once, in "How to read this". */
const OUTCOME_MEANING: Record<Outcome, string> = {
  covered: 'your plan says close to what the standard says',
  partial: 'similar topic or wording; check whether the specific skill is taught',
  weak: 'related to a standard, but loosely — judge it against your own words',
  none: 'no standard came close enough to report',
};

interface ObjectiveReading {
  index: number;
  text: string;
  matches: Finding[]; // strongest first
  outcome: Outcome;
}

/**
 * Join objectives to their findings. Analyses from before objectives were stored have an
 * empty list; their matched objectives can be rebuilt from each finding's evidence (which
 * quotes the objective), though the unmatched ones are unrecoverable.
 */
function readings(analysis: Analysis): ObjectiveReading[] {
  const objectives = analysis.objectives.length > 0
    ? analysis.objectives
    : [...new Map(analysis.findings.map((f) => [f.segmentIndex, { index: f.segmentIndex, text: f.evidence, matchedBy: [] as string[] }])).values()]
        .sort((a, b) => a.index - b.index);

  return objectives.map((objective) => {
    const matches = analysis.findings
      .filter((f) => f.segmentIndex === objective.index)
      .sort((a, b) => b.score - a.score);
    return {
      index: objective.index,
      text: objective.text,
      matches,
      outcome: matches[0]?.band ?? 'none',
    };
  });
}

function Match({ finding }: { finding: Finding }) {
  return (
    <li className="match">
      {/* The standard's own words, not its filing code — 111.5.b.5.B describes nothing. */}
      <p className="standard">{finding.description}</p>
      <p className="provenance">
        <span className="cite-label">
          Published by {finding.author ?? 'the standards authority'}
          {finding.statementType ? ` · ${finding.statementType}` : ''}
        </span>{' '}
        <code className="code" title={finding.statementCode}>{standardLabel(finding.jurisdiction, finding.statementCode)}</code>
      </p>
    </li>
  );
}

function ObjectiveCard({ reading, profile }: { reading: ObjectiveReading; profile: Analysis['profile'] }) {
  const { academicSubject, gradeLevels } = profile;
  return (
    <li className="reading-card" data-outcome={reading.outcome}>
      {/* The circle leads the card; the verdict word holds the top-right corner. */}
      <div className="card-gutter">
        <Icon outcome={reading.outcome} />
      </div>
      <div className="card-body">
        <div className="reading-head">
          <span className="reading-number">Objective {reading.index + 1}</span>
          <span
            className="verdict"
            title={reading.matches[0] ? `match strength ${reading.matches[0].score.toFixed(2)}` : undefined}
          >
            {OUTCOME_LABEL[reading.outcome]}
          </span>
        </div>
        <p className="objective-text ph-no-capture">{reading.text}</p>
        {reading.matches.length > 0 ? (
          <ul className="matches" aria-label={`Standards matching objective ${reading.index + 1}`}>
            {reading.matches.map((finding) => <Match key={finding.caseIdentifierUUID} finding={finding} />)}
          </ul>
        ) : (
          <p className="no-match">
            No {academicSubject} grade {gradeLevels.join(', ')} standard matched this. It may
            be covered in another grade or subject — or it may be something the standards
            simply do not ask for.
          </p>
        )}
      </div>
    </li>
  );
}

const itemOutcome = (item: ChecklistItem): Outcome => item.band ?? 'none';

function ChecklistRow({ item, jurisdiction }: { item: ChecklistItem; jurisdiction: string }) {
  const outcome = itemOutcome(item);
  return (
    <li className="check-item" data-outcome={outcome}>
      <div className="card-gutter">
        <Icon outcome={outcome} />
      </div>
      <div className="card-body">
        <p className="standard">{item.description}</p>
        <p className="check-note">
          {item.matchedSegmentIndexes.length > 0
            ? `Matched by objective ${item.matchedSegmentIndexes.map((i) => i + 1).join(', ')} of your plan`
            : 'Your plan does not say this.'}{' '}
          <code className="code" title={item.statementCode}>{standardLabel(jurisdiction, item.statementCode)}</code>
        </p>
      </div>
    </li>
  );
}

function ChecklistView({
  checklist,
  filter,
  profile,
}: {
  checklist: StandardsChecklist;
  filter: Filter;
  profile: Analysis['profile'];
}) {
  const clusters = checklist.clusters
    .map((cluster) => ({
      ...cluster,
      // The header arithmetic stays about the whole cluster even when a filter narrows
      // the rows: "6 of 10 covered" is the fact; the filter is just what is on screen.
      totalCount: cluster.items.length,
      items: cluster.items.filter((item) => filter === 'all' || itemOutcome(item) === filter),
    }))
    .filter((cluster) => cluster.items.length > 0);

  return (
    <>
      <p className="headline">
        {profile.jurisdiction} {profile.academicSubject} grade {profile.gradeLevels.join(', ')}{' '}
        asks for <strong>{checklist.totalCount} things</strong>. This plan touches{' '}
        <strong>{checklist.coveredCount}</strong> of them.
      </p>
      {clusters.map((cluster) => (
        <section key={cluster.code} className="cluster">
          <h3 className="cluster-head">
            <span className="cluster-title">{cluster.description ?? `Standards ${cluster.code}`}</span>
            <span className="cluster-count">
              {cluster.coveredCount} of {cluster.totalCount} covered
            </span>
          </h3>
          <ul className="check-list" aria-label={`Standards under ${cluster.code}`}>
            {cluster.items.map((item) => <ChecklistRow key={item.caseIdentifierUUID} item={item} jurisdiction={profile.jurisdiction} />)}
          </ul>
        </section>
      ))}
    </>
  );
}

function HowToRead({ onTrack }: Pick<ReportProps, 'onTrack'>) {
  return (
    <details className="legend" onToggle={(event) => onTrack?.(previewObjectivesAction('legend_toggled', { open: event.currentTarget.open }))}>
      <summary>How to read this</summary>
      <ul className="legend-list">
        {(['covered', 'partial', 'weak', 'none'] as Outcome[]).map((outcome) => (
          <li key={outcome} data-outcome={outcome}>
            <Verdict outcome={outcome} />
            <span className="legend-meaning">{OUTCOME_MEANING[outcome]}</span>
          </li>
        ))}
      </ul>
      <p className="legend-note">
        This is a comparison of wording, made by a machine — it is not a judgement about
        teaching. Your own reading of your plan beats it wherever the two disagree.
      </p>
    </details>
  );
}

export default function Report({
  analysis,
  extractedText,
  variant,
  onStartOver,
  onChangeProfile,
  onViewToggled,
  onDelete,
  deleting = false,
  onTrack,
}: ReportProps) {
  const { attribution } = analysis;
  const { jurisdiction, academicSubject, gradeLevels } = analysis.profile;

  const [view, setView] = useState<ViewName>('plan');
  const [filter, setFilter] = useState<Filter>('all');
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const cards = readings(analysis);
  const matchedCount = cards.filter((c) => c.outcome !== 'none').length;
  const objectiveCount = analysis.objectives.length > 0 ? analysis.objectives.length : analysis.segmentCount;

  const checklist = analysis.standardsChecklist;

  const ordered = variant === 'grouped'
    ? [...cards].sort((a, b) => OUTCOME_ORDER.indexOf(a.outcome) - OUTCOME_ORDER.indexOf(b.outcome) || a.index - b.index)
    : cards;
  const visible = ordered.filter((c) => filter === 'all' || c.outcome === filter);

  /** Counts for the filter chips, per the view they filter. */
  const chipCounts: Record<Outcome, number> = { covered: 0, partial: 0, weak: 0, none: 0 };
  if (view === 'plan') {
    for (const c of cards) chipCounts[c.outcome] += 1;
  } else if (checklist) {
    for (const cluster of checklist.clusters) {
      for (const item of cluster.items) chipCounts[itemOutcome(item)] += 1;
    }
  }
  const chipTotal = chipCounts.covered + chipCounts.partial + chipCounts.weak + chipCounts.none;

  const selectFilter = (next: Filter) => {
    setFilter(next);
    onTrack?.(previewObjectivesAction('filter_selected', { filter: next, view }));
  };

  const switchView = (next: ViewName) => {
    if (next === view) return;
    setView(next);
    setFilter('all');
    onViewToggled?.(next);
  };

  /**
   * Matches, but nothing above "possibly related" — the case most likely to mislead,
   * since near-floor results still look like an answer.
   */
  const nothingStrong =
    cards.length > 0 && analysis.counts.covered === 0 && analysis.counts.partial === 0 &&
    analysis.findings.length > 0;
  const mismatch = analysis.subjectMismatch;

  return (
    <div className="panel report"
      onClick={(event) => {
        if (event.target instanceof Element && event.target.closest('a')) {
          onTrack?.(previewObjectivesAction('source_link_clicked'));
        }
      }}

    >
      <h2>What this plan covers</h2>

      {checklist && (
        <div className="view-toggle" role="tablist" aria-label="Reading direction">
          <button
            className="toggle"
            type="button"
            role="tab"
            aria-selected={view === 'plan'}
            onClick={() => switchView('plan')}
          >
            Your plan
          </button>
          <button
            className="toggle"
            type="button"
            role="tab"
            aria-selected={view === 'standards'}
            onClick={() => switchView('standards')}
          >
            The state&apos;s checklist
          </button>
        </div>
      )}

      {view === 'plan' && (
        <p className="headline">
          {objectiveCount > 0 ? (
            <>
              We read <strong>{objectiveCount} objectives</strong> in this plan.{' '}
              <strong>{matchedCount}</strong> matched a {jurisdiction} {academicSubject} grade{' '}
              {gradeLevels.join(', ')} standard
              {matchedCount < objectiveCount
                ? <>; <strong>{objectiveCount - matchedCount}</strong> matched nothing.</>
                : '.'}
            </>
          ) : (
            <>We could not read any objectives out of this plan.</>
          )}
        </p>
      )}

      {analysis.standardsProvider === 'learning-commons' && (
        <details className="legend">
          <summary>About the standards data</summary>
          <p className="legend-note">
            We match your plan against standards in the Learning Commons collection, filtered
            by your state, subject, and grades. Each match shows the publisher and the
            standard&apos;s reference code. Codes differ by state.
          </p>
          <p className="legend-note">
            Use the reference code to look up a standard in the publisher&apos;s materials.
            We show references without links to individual standards.
          </p>
          <p className="legend-note">
            <a href={LEARNING_COMMONS_STANDARDS_URL} target="_blank" rel="noreferrer">
              Read about the standards collection
            </a>{' '}
            in Learning Commons&apos; public documentation. This explains the data source;
            it is not a page for each standard.
          </p>
        </details>
      )}

      {mismatch ? (
        <div className="warn" role="status">
          <strong>
            This plan looks like {mismatch.suggested}, not {academicSubject}.
          </strong>{' '}
          Read against {academicSubject}, nothing matched well; against {mismatch.suggested}{' '}
          it matches strongly. The results below are the closest {academicSubject} has to
          offer, which is not the same as what your plan covers.{' '}
          <button className="link" type="button" onClick={onChangeProfile}>
            Switch to {mismatch.suggested} and read it again
          </button>
          .
        </div>
      ) : nothingStrong ? (
        <div className="warn" role="status">
          <strong>Nothing here is a strong match.</strong> Every match below scraped in at
          the bottom of the range, which most often means the plan is not {academicSubject},
          or not grade {gradeLevels.join(', ')} — rather than that the plan covers little.
          Check the matches against your own words before you read anything into them.{' '}
          <button className="link" type="button" onClick={onChangeProfile}>
            Change the subject or grade and read it again
          </button>
          .
        </div>
      ) : null}

      {analysis.standardsProvider === 'fixture' && (
        <p className="warn" role="status">
          Sample data. No Learning Commons API key is configured, so these standards are a
          small built-in set and not a real search. Nothing here is a finding about your plan.
        </p>
      )}

      {analysis.objectivesProvider === 'heuristic' && view === 'plan' && (
        <p className="warn" role="status">
          Objectives were split out by a simple heading-and-paragraph rule rather than read
          for meaning. Expect coarser matches.
        </p>
      )}

      {analysis.truncated && (
        <p className="warn" role="status">
          This plan was longer than one reading covers. Only the first{' '}
          {analysis.segmentCount} objectives were read. Split a long unit into single lessons
          for a complete reading.
        </p>
      )}

      {analysis.degradedSegments.length > 0 && (
        <p className="warn" role="status">
          {analysis.degradedSegments.length} of {analysis.segmentCount} objectives could not
          be searched. Running the reading again may fill them in.
        </p>
      )}

      {chipTotal > 0 && (
        <>
          <HowToRead onTrack={onTrack} />
          <div className="filters" role="group" aria-label="Filter by verdict">
            <button
              className="chip"
              type="button"
              aria-pressed={filter === 'all'}
              onClick={() => selectFilter('all')}
            >
              All · {chipTotal}
            </button>
            {(['covered', 'partial', 'weak', 'none'] as Outcome[])
              .filter((o) => chipCounts[o] > 0)
              .map((o) => (
                <button
                  key={o}
                  className="chip"
                  type="button"
                  aria-pressed={filter === o}
                  title={OUTCOME_LABEL[o]}
                  onClick={() => selectFilter(filter === o ? 'all' : o)}
                >
                  <Icon outcome={o} /> {OUTCOME_SHORT[o]} · {chipCounts[o]}
                </button>
              ))}
          </div>
        </>
      )}

      {view === 'standards' && checklist ? (
        <ChecklistView checklist={checklist} filter={filter} profile={analysis.profile} />
      ) : (
        cards.length > 0 && (
          <ol className="reading-list" aria-label="Your objectives">
            {visible.map((reading) => (
              <ObjectiveCard key={reading.index} reading={reading} profile={analysis.profile} />
            ))}
          </ol>
        )
      )}

      {cards.length === 0 && analysis.findings.length === 0 && objectiveCount > 0 && (
        <p className="hint">
          Nothing in this plan matched a {jurisdiction} {academicSubject} grade{' '}
          {gradeLevels.join(', ')} standard. That may say more about the reading than about
          the plan. Check the objectives against your original file.
        </p>
      )}

      {/* R15. A CC BY-4.0 licence term, not a nicety — it renders wherever standards do. */}
      {attribution && (analysis.findings.length > 0 || view === 'standards') && (
        <p className="attribution">
          {attribution.statement}{' '}
          <a href={attribution.license} rel="license noreferrer" target="_blank">
            CC BY 4.0
          </a>
        </p>
      )}

      {extractedText && (
        <details className="extracted ph-no-capture">
          <summary>What we read from your file</summary>
          <pre>{extractedText}</pre>
        </details>
      )}

      <div className="report-actions">
        <button className="secondary" type="button" onClick={onStartOver}>
          Read another plan
        </button>

        {onDelete && !confirmingDelete && (
          <button className="link danger" type="button" onClick={() => { setConfirmingDelete(true); onTrack?.(previewObjectivesAction('delete_clicked')); }}>
            Delete this plan
          </button>
        )}
        {onDelete && confirmingDelete && (
          <span className="confirm-delete" role="group" aria-label="Confirm deletion">
            This removes the saved report, including its objectives and evidence excerpts. It cannot be undone.{' '}
            <button className="link danger" type="button" disabled={deleting} onClick={onDelete}>
              {deleting ? 'Deleting…' : 'Yes, delete it'}
            </button>{' '}
            <button className="link" type="button" disabled={deleting} onClick={() => { setConfirmingDelete(false); onTrack?.(previewObjectivesAction('delete_cancelled')); }}>
              Keep it
            </button>
          </span>
        )}
      </div>
    </div>
  );
}
